import React, { useState, useEffect } from "react";
import { api, apiError } from "../api/client";
import { X, Archive, Calendar, Stethoscope } from "lucide-react";
import { toast } from "sonner";

const INPUT = "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-11 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";
const BTN_PRIMARY = "h-11 px-4 rounded-xl bg-brand text-white font-semibold text-sm hover:bg-brand/90 disabled:opacity-50 flex items-center gap-2";
const BTN_SECONDARY = "h-11 px-4 rounded-xl border border-[var(--border-default)] bg-white text-ink font-semibold text-sm hover:bg-subtle disabled:opacity-50 flex items-center gap-2";

const LABELS = {
  appointments: "rendez-vous",
  consultations: "consultations",
  invoices: "factures",
  payments: "paiements",
};

const STATUS_DEFAULTS = {
  appointments: ["completed", "cancelled", "no_show"],
  invoices: ["paid"],
  consultations: null,
  payments: null,
};

/**
 * Generic Bulk Archive dialog used by Appointments, Consultations, Invoices, Payments pages.
 * Props:
 *   kind: "appointments" | "consultations" | "invoices" | "payments"
 *   onClose(), onDone()
 *   doctors?: [{id, first_name, last_name}]   // only for appointments/consultations
 */
export default function BulkArchiveDialog({ kind, onClose, onDone, doctors = [] }) {
  const label = LABELS[kind] || kind;
  const showDoctorFilter = ["appointments", "consultations"].includes(kind);
  const allowedStatuses = STATUS_DEFAULTS[kind];

  // Default: 30 days ago
  const defaultBefore = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [beforeDate, setBeforeDate] = useState(defaultBefore);
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const fetchPreview = async () => {
    setLoadingPreview(true);
    try {
      const body = { before_date: beforeDate ? `${beforeDate}T23:59:59Z` : null };
      if (showDoctorFilter && selectedDoctors.length > 0) body.doctor_ids = selectedDoctors;
      const { data } = await api.post(`/archive/${kind}/bulk-preview`, body);
      setPreview(data.would_archive);
    } catch (err) {
      toast.error(apiError(err, "Erreur preview"));
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Auto-preview when date or filters change
  useEffect(() => {
    fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beforeDate, selectedDoctors]);

  const confirm = async () => {
    if (preview === 0 || preview == null) return;
    if (!window.confirm(`Archiver ${preview} ${label} ?\n\nIls deviendront invisibles dans la liste active et iront dans l'onglet « Archivés ». Vous pourrez les désarchiver à tout moment.`)) return;
    setArchiving(true);
    try {
      const body = { before_date: beforeDate ? `${beforeDate}T23:59:59Z` : null };
      if (showDoctorFilter && selectedDoctors.length > 0) body.doctor_ids = selectedDoctors;
      const { data } = await api.post(`/archive/${kind}/bulk`, body);
      toast.success(`${data.archived} ${label} archivés`);
      onDone();
    } catch (err) {
      toast.error(apiError(err, "Erreur"));
    } finally {
      setArchiving(false);
    }
  };

  const toggleDoctor = (id) => {
    setSelectedDoctors((s) => s.includes(id) ? s.filter((d) => d !== id) : [...s, id]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50" data-testid="bulk-archive-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-[var(--border-default)]">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <Archive className="w-5 h-5 text-brand" /> Archiver des {label}
          </h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-ink-muted uppercase tracking-wider flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Archiver tous les {label} antérieurs au
            </label>
            <input type="date" value={beforeDate} onChange={(e) => setBeforeDate(e.target.value)}
              className={INPUT} data-testid="bulk-before-date" />
            <div className="text-[11px] text-ink-muted mt-1">Les éléments du jour et postérieurs ne sont pas touchés.</div>
          </div>

          {allowedStatuses && (
            <div className="text-xs text-ink-muted bg-subtle p-3 rounded-xl">
              <div className="font-semibold text-ink mb-1">Statuts archivables</div>
              <div>{allowedStatuses.join(" · ")}</div>
              <div className="italic mt-1">Les autres statuts sont ignorés automatiquement.</div>
            </div>
          )}

          {showDoctorFilter && doctors.length > 0 && (
            <div>
              <label className="text-xs text-ink-muted uppercase tracking-wider flex items-center gap-1">
                <Stethoscope className="w-3 h-3" /> Praticien (optionnel — tous si rien coché)
              </label>
              <div className="mt-2 flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {doctors.map((d) => (
                  <button key={d.id} type="button" onClick={() => toggleDoctor(d.id)}
                    data-testid={`bulk-doctor-${d.id}`}
                    className={`h-9 px-3 rounded-lg text-sm font-semibold border ${selectedDoctors.includes(d.id) ? "bg-brand text-white border-brand" : "bg-white text-ink border-[var(--border-default)]"}`}>
                    Dr {d.last_name || d.first_name}
                  </button>
                ))}
              </div>
              {selectedDoctors.length > 0 && (
                <button type="button" onClick={() => setSelectedDoctors([])}
                  className="text-xs text-brand mt-2 underline">Tout désélectionner</button>
              )}
            </div>
          )}

          <div className="p-4 rounded-xl bg-brand/5 border border-brand/20">
            <div className="text-xs text-ink-muted uppercase tracking-wider">Aperçu</div>
            <div className="font-heading font-bold text-2xl text-brand" data-testid="bulk-preview-count">
              {loadingPreview ? "…" : (preview ?? 0)}
            </div>
            <div className="text-sm text-ink">{label} seront archivés</div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border-default)]">
            <button onClick={onClose} className={BTN_SECONDARY}>Annuler</button>
            <button onClick={confirm} disabled={archiving || !preview} data-testid="bulk-archive-confirm"
              className={BTN_PRIMARY}>
              <Archive className="w-4 h-4" /> {archiving ? "Archivage…" : `Archiver ${preview || 0}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Small inline button to archive/unarchive ONE record.
 * Used per row in lists.
 */
export function ArchiveRowButton({ kind, itemId, archived = false, onDone, disabled = false }) {
  const [loading, setLoading] = useState(false);
  const handle = async (e) => {
    e?.stopPropagation();
    if (loading || disabled) return;
    const label = LABELS[kind] || kind;
    if (archived) {
      setLoading(true);
      try {
        await api.post(`/archive/${kind}/${itemId}/unarchive`);
        toast.success("Désarchivé");
        onDone?.();
      } catch (err) { toast.error(apiError(err, "Erreur")); }
      finally { setLoading(false); }
    } else {
      if (!window.confirm(`Archiver cet élément ?\n\nIl sera retiré de la liste active mais conservé dans les archives.`)) return;
      setLoading(true);
      try {
        await api.post(`/archive/${kind}/${itemId}`);
        toast.success("Archivé");
        onDone?.();
      } catch (err) { toast.error(apiError(err, "Erreur")); }
      finally { setLoading(false); }
    }
  };
  return (
    <button onClick={handle} disabled={loading || disabled}
      data-testid={`row-archive-${itemId}`}
      title={archived ? "Désarchiver" : "Archiver"}
      className="p-2 rounded-lg hover:bg-subtle text-ink-muted hover:text-brand disabled:opacity-50">
      <Archive className={`w-4 h-4 ${archived ? "text-brand" : ""}`} />
    </button>
  );
}

/**
 * Tab switcher Actifs / Archivés
 */
export function ArchiveTabs({ value, onChange, activeCount, archivedCount }) {
  return (
    <div className="flex gap-1 p-1 rounded-xl bg-subtle inline-flex">
      <button onClick={() => onChange("active")} data-testid="archive-tab-active"
        className={`h-9 px-3 rounded-lg text-sm font-semibold transition-colors ${value !== "archived" ? "bg-white shadow-sm text-brand" : "text-ink-muted"}`}>
        Actifs{activeCount != null ? ` (${activeCount})` : ""}
      </button>
      <button onClick={() => onChange("archived")} data-testid="archive-tab-archived"
        className={`h-9 px-3 rounded-lg text-sm font-semibold transition-colors ${value === "archived" ? "bg-white shadow-sm text-brand" : "text-ink-muted"}`}>
        <Archive className="w-3 h-3 inline mr-1" />
        Archivés{archivedCount != null ? ` (${archivedCount})` : ""}
      </button>
    </div>
  );
}
